import React, { memo } from 'react';

import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import dynamic from 'next/dynamic';

import { useFeatures } from '@atc/features';

import { vdpResultsDuck } from '@/ducks/vdp';

import Anchor from '@/components/Anchor/index.js';
import LazyComponent from '@/components/LazyComponent';

const FraudReportContainer = dynamic(() => import('@/containers/FraudReportContainer'), { ssr: false });

const AdditionalResourcesContainer = dynamic(() => import('@/containers/vdp/AdditionalResourcesContainer'), { ssr: false });
const HomeServicesContainer = dynamic(() => import('@/containers/vdp/HomeServicesContainer'), { ssr: false });
const OwnerDetailsContainer = dynamic(() => import('@/containers/vdp/OwnerDetailsContainer'), { ssr: false });
const PreorderSearchContainer = dynamic(() => import('@/containers/vdp/PreorderSearchContainer'), { ssr: false });
const VehicleExchangeContainer = dynamic(() => import('@/containers/vdp/VehicleExchangeContainer'), { ssr: false });

const PsxLowerDetails = dynamic(() => import('@/containers/vdp/psx/PsxLowerDetails'), { ssr: false });

/* This container renders the lower section of the VDP
 * (all components below where the sticky sidebar stops)
 * Any components in this section should be wrapped in a
 * PageSection for proper spacing unless a new .row / .col-*
 * layout is introduced for the page
 */
function LowerDetails({
    onEmailCTAClick,
    chatClickHandler,
    phoneAndTextHandler,
}) {
    const {
        additional_resources: [isAdditionalResourcesEnabled],
    } = useFeatures([
        'additional_resources',
    ]);

    const {
        hasVehicleExchange,
        owner: {
            customOrderStatus: { newCarCustomOrder, newCarBoost } = {},
            privateSeller: isPrivateSeller,
        } = {},
    } = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory) || {};

    const showPreorderCard = newCarCustomOrder || newCarBoost;

    if (isPrivateSeller) {
        return <PsxLowerDetails />;
    }

    return (
        <>
            <LazyComponent key="owner_details_lazy">
                <OwnerDetailsContainer
                    chatClickHandler={chatClickHandler}
                    phoneClickHandler={phoneAndTextHandler}
                    textClickHandler={phoneAndTextHandler}
                    onEmailCTAClick={onEmailCTAClick}
                />
            </LazyComponent>

            {hasVehicleExchange && (
                <LazyComponent key="vehicle_exchange">
                    <VehicleExchangeContainer />
                </LazyComponent>
            )}

            {showPreorderCard && <LazyComponent key="preorder_lazy"><PreorderSearchContainer /></LazyComponent>}

            <LazyComponent
                fallback={<Anchor id="homeServices" />}
                key="ahs"
            >
                <HomeServicesContainer
                    onEmailCTAClick={onEmailCTAClick}
                />
            </LazyComponent>

            {isAdditionalResourcesEnabled && <LazyComponent key="addl_resources"><AdditionalResourcesContainer /></LazyComponent>}

            <LazyComponent key="fraud"><FraudReportContainer /></LazyComponent>
        </>
    );
}

LowerDetails.propTypes = {
    /**
     * The callback function fires when email CTA is clicked
     */
    onEmailCTAClick: PropTypes.func,
    /**
     * The callback function fires when chat CTA is clicked
     */
    chatClickHandler: PropTypes.func,
    /**
     * The callback function fires when owner phone and text CTA is called
     */
    phoneAndTextHandler: PropTypes.func,
};

export default memo(LowerDetails);
